import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import DetailsPage from "../../../components/DetailsPage";
import { DetailsPageMenuItem } from "../../../components/DetailsPage/menu-items/useMenuItems";
import useGetCompanyDetails from "./http/useGetCompanyDetails";
import InfoBox from "../../../components/Infobox";
import mapLocationToEntries from "../../../components/Infobox/utils/mapLocationToEntries";
import AssociatedSiteSearchTable from "./components/AssociatedSitesTable";
import Box, { BoxPadding } from "../../../components/Box";
import EntityStatus from "../../../Enums/EntityStatus";
import axiosApi from "../../../components/Api/Axios";
import BackendServices from "../../../components/Api/BackendService";
import { UserUrls } from "../../UserManagement";
import UserParent from "../../UserManagement/UserNew/util/UserParent";
import Icon, { IconType } from "../../../components/Icon";
import { DropdownMenuItem, DropdownMenuItemType } from "../../../components/DropdownMenu";
import Permission from "../../../Authentication/model/Permission";
import PermissionArea from "../../../Authentication/model/PermissionArea";
import { CompanyUrls } from "../index";
import mapNamelessContactToEntries from "../../../components/Infobox/utils/mapNamelessContactToEntries";
import axios from "axios";
import useGetData from "../../../http/useGetData";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/reducers";
import { dispatch } from "../../../components/App/App";
import { setVerifyLimit } from "../../../store/details/actions/getTableFilterInputs";
import { isMegatronFeatureOn } from "../../../Authentication/Auth";
import { isBRCGSAdmin } from "../../../Authentication/Auth";

const sharedCompanyDetailsMenu: DetailsPageMenuItem[] = [
  DetailsPageMenuItem.SHARE,
  DetailsPageMenuItem.EDIT,
  DetailsPageMenuItem.ARCHIVE,
];
const isAllowedExportValue = async (id: string): Promise<any> => {
  const response = await axiosApi.get<any>(
    `${BackendServices.COMPANY_SERVICE.COMPANY}/getallowexport/${id}`
  );
  return response.data; // Assuming response.data is the boolean value
};

const currentVerifyLimitValue = async (id: string): Promise<any> => {
  const response = await axiosApi.get<any>(
    `${BackendServices.COMPANY_SERVICE.COMPANY}/getVerifyFavouriteLimit/${id}`
  );
  return response.data.favouriteTagLimit;
};

interface DetailItem {
    label: string,
    value: string | number,
    required: true
}

const CompanyDetails = ({
  match: {
    params: { id },
  },
}: RouteComponentProps<{ readonly id: string }>) => {
  const [isAllowedExport, getAllowExport] = useState<any>(false);
  const [currentVerifyLimit, getVerifyLimit] = useState<any>();
  const [megatronFeatureOn, setMegatronFeatureOn] = useState<boolean>(false);

  const newVerifyLimit: string = useSelector(
    (state: RootState) => state.details.verifyLimit
  );

  useEffect(() => {
    fetchAllowExportStatus();
    fetchVerifyLimit();
  }, [id]);

  const fetchAllowExportStatus = async () => {
    const isAllowedExport = await isAllowedExportValue(id);
    getAllowExport(isAllowedExport);
  };

  const fetchVerifyLimit = async () => {
    const currentVerifyLimit = await currentVerifyLimitValue(id);
    getVerifyLimit(currentVerifyLimit);
  };

  const { isLoading, company, refresh } = useGetCompanyDetails(id);
  if (isLoading || !company)
    return <DetailsPage isLoading={isLoading} title="" />;
  const isArchived = company.status === EntityStatus.ARCHIVED;

  const postArchive = () => {
    axiosApi
      .post(
        `${BackendServices.COMPANY_SERVICE.COMPANY}/${id}/${
          isArchived ? "unarchive" : "archive"
        }`
      )
      .catch(() => {})
      .finally(refresh);
  };

  const text = isAllowedExport.isAllowExport ? "Prevent" : "Allow";
  const dialogHeader = `Are you sure you want to ${text.toLowerCase()} export list (verify - non favourites)?`;
  const toggleAllowPreventExport = () => {
    axiosApi
      .post(
        `${BackendServices.COMPANY_SERVICE.COMPANY}/${id}/${
          isAllowedExport.isAllowExport ? "preventexport" : "allowexport"
        }`
      )
      .then(() => {
        fetchAllowExportStatus();
      })
      .catch(() => {})
      .finally(refresh);
  };

  const updateVerifyLimit = () => {
    if (newVerifyLimit != undefined && newVerifyLimit.trim() != "") {
      axiosApi
        .post(
          `${BackendServices.COMPANY_SERVICE.COMPANY}/setVerifyFavouriteLimit`,
          {
            CompanyExternalId: Number(id),
            FavouriteTagLimit: Number(newVerifyLimit),
          }
        )
        .then(() => {
          fetchVerifyLimit();
          dispatch(setVerifyLimit(""));
        })
        .catch(() => {})
        .finally(refresh);
    }
  };

  // Checking if the megatron feature flag in ON
  const fetchMegatronFeatureOn = async () => {
      const getMegatronFeature = await isMegatronFeatureOn();
      setMegatronFeatureOn(getMegatronFeature);
    };
  
    fetchMegatronFeatureOn();

  // Regular detail items that need to be shown normally
  const detailItems: DetailItem[] = [
    { label: "Company ID", value: company.id, required: true },
    { label: "Status", value: company.status, required: true },
  ];

  // Details items to be shown ONLY when megatron feature flag is on.
  const megatronDetailItems: DetailItem[] = [
    {
      label: "Max Favourites",
      value: company.maxFavouriteLimit.toString(),
      required: true,
    },
    { label: "Type", value: company.type, required: true },
  ];
   var menuItemsList : DropdownMenuItem[] =
      [
              {
                icon: <Icon type={IconType.USERS} />,
                label: "View Users",
                type: DropdownMenuItemType.LINK,
                state: { name: company.name },
                link: CompanyUrls.users(company.id),
                permissionRequired: [
                  {
                    permission: Permission.VIEW,
                    area: PermissionArea.USER,
                  },
                ],
              },
              {
                icon: <Icon type={IconType.USER_ADD} />,
                label: "Create User",
                type: DropdownMenuItemType.LINK,
                link: UserUrls.create({ [UserParent.COMPANY]: id }),
                permissionRequired: [
                  {
                    permission: Permission.GENERATE,
                    area: PermissionArea.USER,
                  },
                ],
              },              
            ]
    if(isBRCGSAdmin())
      {
        menuItemsList =  menuItemsList.concat([{
                icon: <Icon type={IconType.FileExport} />,
                label: `${text} Export List`,
                type: DropdownMenuItemType.BUTTON_CONFIRM,
                dialogHeader,
                onConfirm: toggleAllowPreventExport,
                declineButtonText: "Cancel",
                acceptButtonText: text,
                permissionRequired: [
                  {
                    permission: Permission.GENERATE,
                    area: PermissionArea.COMPANY,
                  },
                ],
              },
              {
                icon: <Icon type={IconType.Settings} />,
                label: "Set Certificate Tracker Limit",
                type: DropdownMenuItemType.BUTTON_VERIFY_LIMIT_CONFIRM,
                companyId: id,
                currentVerifyFavLimit: currentVerifyLimit,
                onConfirm: updateVerifyLimit,
                declineButtonText: "Cancel",
                acceptButtonText: "Confirm",
                permissionRequired: [
                  {
                    permission: Permission.GENERATE,
                    area: PermissionArea.COMPANY,
                  },
                ]                
              },])
      }
      
  return (
    <DetailsPage
      id={company.id}
      title={company.name}
      isLoading={isLoading}
      detailsPageMenuItems={[
        DetailsPageMenuItem.CHANGE_LOG,
        ...(company.isShared ? sharedCompanyDetailsMenu : []),
      ]}
      archiveConfig={{
        entityName: "company",
        isArchived,
        onClick: postArchive,
      }}
      menuItems = {
        !company.isShared
          ? []
          : menuItemsList          
      }
      contentTabs={[
        {
          content: (
            <Box padding={BoxPadding.None} header="Associated Sites">
              <AssociatedSiteSearchTable
                companyId={id}
                companyName={company.name}
                isShared={company.isShared}
              />
            </Box>
          ),
        },
      ]}
    >
      <InfoBox
        header="Details"
        columns={megatronFeatureOn ? 2 : 1}
        items={megatronFeatureOn ? detailItems.concat(megatronDetailItems) : detailItems}
      />
      <InfoBox
        header="Contact"
        columns={2}
        items={[
          ...mapLocationToEntries(company.location),
          ...mapNamelessContactToEntries(company.contact),
          {
            label: "Website",
            value: company.website
              ? {
                  label: company.website,
                  link: company.website,
                  external: true,
                }
              : "",
          },
        ]}
      />
    </DetailsPage>
  );
};

export default CompanyDetails;
